/**
 * @module @liquidefy/code-editor/terminal/guided/preview
 * @version 1.0.0
 * @license LQD Research LLC
 * @rust preview.rs — false; 4/2/26; sm
 *
 * Inline command previews — shows what a command will do BEFORE execution.
 * Parses command lines, determines affected resources, suggests dry-run flags.
 * All functions are pure. No DOM, no side effects.
 */

// ═══ §1 TYPES ═══

export type PreviewType =
  | 'file-content'
  | 'directory-listing'
  | 'file-delete'
  | 'file-move'
  | 'git-status'
  | 'search-results'
  | 'process-list'
  | 'disk-usage'
  | 'network'
  | 'package-info'
  | 'dry-run'
  | 'none';

export interface CommandPreview {
  readonly type: PreviewType;
  readonly command: string;
  readonly summary: string;
  readonly details: string[];
  readonly affectedPaths: string[];
  readonly riskLevel: 'safe' | 'moderate' | 'dangerous';
  readonly canDryRun: boolean;
  readonly dryRunFlag?: string;
  readonly estimatedDuration?: string;
}

export interface PreviewState {
  readonly enabled: boolean;
  readonly currentPreview: CommandPreview | null;
  readonly previewHistory: CommandPreview[];
  readonly maxHistory: number;
}

export interface ParsedCommand {
  readonly command: string;
  readonly args: string[];
  readonly flags: string[];
  readonly pipes: string[];
}

export interface PreviewSummary {
  readonly enabled: boolean;
  readonly hasPreview: boolean;
  readonly type: PreviewType | null;
}

// ═══ §2 DRY-RUN FLAG MAP ═══

const DRY_RUN_FLAGS: Record<string, string> = {
  rm:       '--interactive',
  rsync:    '--dry-run',
  'npm publish': '--dry-run',
  'pnpm publish': '--dry-run',
  'yarn publish': '--dry-run',
  'git clean': '-n',
  'git merge': '--no-commit',
  'docker system prune': '--dry-run',
  make:     '-n',
  'pip install': '--dry-run',
  'pip uninstall': '--dry-run',
  ansible:  '--check',
  terraform: '-plan',
  kubectl:  '--dry-run=client',
  'cargo install': '--dry-run',
};

// ═══ §3 COMMAND LINE PARSING ═══

export function parseCommandLine(input: string): ParsedCommand {
  const trimmed = input.trim();
  if (!trimmed) return { command: '', args: [], flags: [], pipes: [] };

  // Split on pipes first
  const pipeSegments = splitOnPipes(trimmed);
  const firstSegment = pipeSegments[0]!.trim();
  const pipes = pipeSegments.slice(1).map(s => s.trim());

  // Tokenize the first segment
  const tokens = tokenize(firstSegment);
  if (tokens.length === 0) return { command: '', args: [], flags: [], pipes };

  const command = tokens[0]!;
  const flags: string[] = [];
  const args: string[] = [];

  for (let i = 1; i < tokens.length; i++) {
    const t = tokens[i]!;
    if (t.startsWith('-')) {
      flags.push(t);
    } else {
      args.push(t);
    }
  }

  return { command, args, flags, pipes };
}

function splitOnPipes(input: string): string[] {
  const segments: string[] = [];
  let current = '';
  let inSingle = false;
  let inDouble = false;

  for (let i = 0; i < input.length; i++) {
    const ch = input[i];
    if (ch === "'" && !inDouble) { inSingle = !inSingle; current += ch; }
    else if (ch === '"' && !inSingle) { inDouble = !inDouble; current += ch; }
    else if (ch === '|' && !inSingle && !inDouble && input[i + 1] !== '|') {
      segments.push(current);
      current = '';
    } else if (ch === '&' && input[i + 1] === '&' && !inSingle && !inDouble) {
      segments.push(current);
      current = '';
      i++; // skip second &
    } else if (ch === ';' && !inSingle && !inDouble) {
      segments.push(current);
      current = '';
    } else {
      current += ch;
    }
  }
  if (current.trim()) segments.push(current);
  return segments.length > 0 ? segments : [''];
}

function tokenize(input: string): string[] {
  const tokens: string[] = [];
  let current = '';
  let inQuote: string | null = null;

  for (let i = 0; i < input.length; i++) {
    const ch = input[i];
    if (inQuote) {
      if (ch === inQuote) { inQuote = null; } else { current += ch; }
    } else if (ch === '"' || ch === "'") {
      inQuote = ch;
    } else if (ch === ' ' || ch === '\t') {
      if (current) { tokens.push(current); current = ''; }
    } else if (ch === '>' || ch === '<') {
      if (current) { tokens.push(current); current = ''; }
      // Skip redirect targets
      if (ch === '>' && input[i + 1] === '>') i++;
      // Skip whitespace after redirect
      while (i + 1 < input.length && input[i + 1] === ' ') i++;
      // Skip the redirect target token
      let target = '';
      i++;
      while (i < input.length && input[i] !== ' ') { target += input[i]; i++; }
    } else {
      current += ch;
    }
  }
  if (current) tokens.push(current);
  return tokens;
}

// ═══ §4 STATE MANAGEMENT ═══

export function createPreviewState(enabled: boolean = true): PreviewState {
  return { enabled, currentPreview: null, previewHistory: [], maxHistory: 50 };
}

export function enablePreviews(state: PreviewState): PreviewState {
  return { ...state, enabled: true };
}

export function disablePreviews(state: PreviewState): PreviewState {
  return { ...state, enabled: false };
}

export function setPreview(state: PreviewState, preview: CommandPreview): PreviewState {
  const history = [preview, ...state.previewHistory].slice(0, state.maxHistory);
  return { ...state, currentPreview: preview, previewHistory: history };
}

export function clearPreview(state: PreviewState): PreviewState {
  return { ...state, currentPreview: null };
}

export function getPreviewHistory(state: PreviewState): CommandPreview[] {
  return state.previewHistory;
}

export function getPreviewSummary(state: PreviewState): PreviewSummary {
  return {
    enabled: state.enabled,
    hasPreview: state.currentPreview !== null,
    type: state.currentPreview?.type ?? null,
  };
}

// ═══ §5 MAIN PREVIEW GENERATOR ═══

export function generatePreview(command: string, cwd?: string): CommandPreview {
  const parsed = parseCommandLine(command);
  const base = parsed.command;

  switch (base) {
    case 'cat': case 'less': case 'head': case 'tail': case 'bat': case 'more':
      return previewFileCommand(base, parsed.args);
    case 'ls': case 'dir': case 'tree': case 'exa':
      return previewDirectoryCommand(base, parsed.args, cwd);
    case 'rm': case 'rmdir': case 'unlink':
      return previewDeleteCommand(base, parsed.args);
    case 'mv':
      return previewMoveCommand('mv', parsed.args);
    case 'cp':
      return previewMoveCommand('cp', parsed.args);
    case 'git':
      return previewGitCommand('git', parsed.args);
    case 'grep': case 'rg': case 'ag': case 'find': case 'fd':
      return previewSearchCommand(base, parsed.args);
    case 'npm': case 'pnpm': case 'yarn': case 'pip': case 'pip3': case 'cargo': case 'brew':
      return previewPackageCommand(base, parsed.args);
    case 'curl': case 'wget': case 'ping': case 'ssh': case 'scp':
      return previewNetworkCommand(base, parsed.args);
    case 'du': case 'df': case 'mount': case 'umount':
      return previewDiskCommand(base, parsed.args);
    case 'ps': case 'top': case 'htop': case 'kill': case 'killall':
      return previewProcessCommand(base, parsed.args);
    case 'docker': case 'docker-compose':
      return previewDockerCommand(base, parsed.args);
    case 'chmod': case 'chown': case 'chgrp':
      return previewPermissionCommand(base, parsed.args);
    default:
      return makePreview('none', command, `Execute: ${base}`, [], [], 'safe', false);
  }
}

// ═══ §6 DOMAIN-SPECIFIC PREVIEW GENERATORS ═══

export function previewFileCommand(command: string, args: string[]): CommandPreview {
  const files = args.filter(a => !a.startsWith('-'));
  const summary = files.length > 0
    ? `Display contents of ${files.join(', ')}`
    : `${command}: no file specified`;

  const details = files.map(f => `Read: ${f}`);
  if (command === 'head') details.push('Shows first 10 lines by default');
  if (command === 'tail') details.push('Shows last 10 lines by default');

  return makePreview('file-content', `${command} ${args.join(' ')}`, summary, details, files, 'safe', false);
}

function previewDirectoryCommand(command: string, args: string[], cwd?: string): CommandPreview {
  const dirs = args.filter(a => !a.startsWith('-'));
  const target = dirs.length > 0 ? dirs.join(', ') : (cwd ?? '.');
  const summary = `List contents of ${target}`;
  const details = [`Target: ${target}`];

  return makePreview('directory-listing', `${command} ${args.join(' ')}`, summary, details, dirs.length > 0 ? dirs : [cwd ?? '.'], 'safe', false);
}

export function previewDeleteCommand(command: string, args: string[]): CommandPreview {
  const targets = args.filter(a => !a.startsWith('-'));
  const flags = args.filter(a => a.startsWith('-')).join(' ');
  const isRecursive = /r/.test(flags) || flags.includes('--recursive');
  const isForced = /f/.test(flags) || flags.includes('--force');

  const risk: 'safe' | 'moderate' | 'dangerous' =
    (isRecursive && isForced) ? 'dangerous' :
    isRecursive ? 'moderate' : 'moderate';

  const summary = targets.length > 0
    ? `Delete ${isRecursive ? 'recursively ' : ''}${targets.join(', ')}${isForced ? ' (forced, no prompts)' : ''}`
    : `${command}: no targets specified`;

  const details: string[] = [];
  for (const t of targets) {
    details.push(`DELETE: ${t}${isRecursive ? ' (and all contents)' : ''}`);
  }
  if (isForced) details.push('WARNING: --force skips all confirmation prompts');
  if (targets.some(t => t === '/' || t === '/*' || t === '~' || t === '~/*')) {
    details.push('CRITICAL: Target includes critical system/home directory!');
  }

  const dryRun = getDryRunFlag(command);
  return makePreview('file-delete', `${command} ${args.join(' ')}`, summary, details, targets, risk, !!dryRun, dryRun ?? undefined, estimateDuration(command));
}

export function previewMoveCommand(command: string, args: string[]): CommandPreview {
  const paths = args.filter(a => !a.startsWith('-'));
  const isCopy = command === 'cp';
  const verb = isCopy ? 'Copy' : 'Move';

  if (paths.length < 2) {
    return makePreview('file-move', `${command} ${args.join(' ')}`, `${verb}: insufficient arguments`, [`${verb} requires source and destination`], [], 'safe', false);
  }

  const destination = paths[paths.length - 1];
  const sources = paths.slice(0, -1);

  const summary = `${verb} ${sources.join(', ')} -> ${destination}`;
  const details = sources.map(s => `${s} -> ${destination}`);
  if (!isCopy) details.push('Original files will be removed after move');

  return makePreview('file-move', `${command} ${args.join(' ')}`, summary, details, [...sources, destination ?? ''], isCopy ? 'safe' : 'moderate', false);
}

export function previewGitCommand(_command: string, args: string[]): CommandPreview {
  const subcommand = args[0] ?? '';
  const subArgs = args.slice(1);
  const fullCmd = `git ${args.join(' ')}`;

  switch (subcommand) {
    case 'push': {
      const isForce = subArgs.some(a => a === '--force' || a === '-f');
      const remote = subArgs.find(a => !a.startsWith('-')) ?? 'origin';
      const branch = subArgs.filter(a => !a.startsWith('-'))[1] ?? 'current branch';
      const summary = `Push ${branch} to ${remote}${isForce ? ' (FORCE)' : ''}`;
      const details = isForce
        ? [`Force pushing to ${remote}/${branch}`, 'WARNING: This rewrites remote history']
        : [`Pushing to ${remote}/${branch}`];
      const risk: 'safe' | 'moderate' | 'dangerous' = isForce ? 'dangerous' : 'safe';
      return makePreview('git-status', fullCmd, summary, details, [], risk, false);
    }
    case 'reset': {
      const isHard = subArgs.includes('--hard');
      const target = subArgs.find(a => !a.startsWith('-')) ?? 'HEAD';
      const summary = `Reset to ${target}${isHard ? ' (HARD — discards changes)' : ''}`;
      const details = isHard
        ? [`Hard reset to ${target}`, 'WARNING: All uncommitted changes will be permanently lost']
        : [`Soft reset to ${target}`, 'Staged changes preserved'];
      return makePreview('git-status', fullCmd, summary, details, [], isHard ? 'dangerous' : 'moderate', false);
    }
    case 'checkout': case 'switch': {
      const branch = subArgs.find(a => !a.startsWith('-')) ?? '';
      const isNew = subArgs.includes('-b') || subArgs.includes('-c');
      const summary = isNew ? `Create and switch to new branch: ${branch}` : `Switch to branch: ${branch}`;
      return makePreview('git-status', fullCmd, summary, [summary], [], 'safe', false);
    }
    case 'merge': {
      const branch = subArgs.find(a => !a.startsWith('-')) ?? '';
      const summary = `Merge ${branch} into current branch`;
      return makePreview('git-status', fullCmd, summary, [summary, 'May produce merge conflicts'], [], 'moderate', true, '--no-commit');
    }
    case 'rebase': {
      const target = subArgs.find(a => !a.startsWith('-')) ?? '';
      const summary = `Rebase current branch onto ${target}`;
      return makePreview('git-status', fullCmd, summary, [summary, 'Rewrites commit history'], [], 'moderate', false);
    }
    case 'stash': {
      const action = subArgs[0] ?? 'push';
      if (action === 'drop' || action === 'clear') {
        return makePreview('git-status', fullCmd, `Stash ${action}: permanently removes stashed changes`, [`Stash ${action}`], [], 'moderate', false);
      }
      return makePreview('git-status', fullCmd, `Stash ${action}: save/restore uncommitted changes`, [`Stash ${action}`], [], 'safe', false);
    }
    case 'clean': {
      const isDryRun = subArgs.includes('-n') || subArgs.includes('--dry-run');
      const isForce = subArgs.includes('-f');
      if (isDryRun) {
        return makePreview('git-status', fullCmd, 'Dry run: show what untracked files would be removed', ['No files will be deleted'], [], 'safe', false);
      }
      return makePreview('git-status', fullCmd, `Remove untracked files${isForce ? ' (forced)' : ''}`, ['Permanently deletes untracked files'], [], 'dangerous', true, '-n');
    }
    case 'diff':
      return makePreview('git-status', fullCmd, 'Show changes between commits, working tree, etc.', ['Shows diff output'], [], 'safe', false);
    case 'log':
      return makePreview('git-status', fullCmd, 'Show commit history', ['Displays commit log'], [], 'safe', false);
    case 'status':
      return makePreview('git-status', fullCmd, 'Show working tree status', ['Lists changed, staged, and untracked files'], [], 'safe', false);
    case 'add':
      return makePreview('git-status', fullCmd, `Stage files: ${subArgs.filter(a => !a.startsWith('-')).join(', ') || 'all'}`, ['Adds files to staging area'], [], 'safe', false);
    case 'commit':
      return makePreview('git-status', fullCmd, 'Create a new commit from staged changes', ['Records staged changes in history'], [], 'safe', false);
    case 'pull':
      return makePreview('git-status', fullCmd, 'Fetch and merge remote changes', ['May produce merge conflicts'], [], 'safe', false);
    default:
      return makePreview('git-status', fullCmd, `Git: ${subcommand}`, [`Execute git ${subcommand}`], [], 'safe', false);
  }
}

export function previewSearchCommand(command: string, args: string[]): CommandPreview {
  const nonFlags = args.filter(a => !a.startsWith('-'));
  const pattern = nonFlags[0] ?? '';
  const target = nonFlags[1] ?? '.';
  const fullCmd = `${command} ${args.join(' ')}`;

  if (command === 'find' || command === 'fd') {
    const nameArg = extractFlagValue(args, '-name') ?? extractFlagValue(args, '--name') ?? nonFlags[0] ?? '*';
    const searchDir = (command === 'find' ? nonFlags[0] : nonFlags[1]) ?? '.';
    return makePreview('search-results', fullCmd, `Find files matching "${nameArg}" in ${searchDir}`, [`Search directory: ${searchDir}`, `Pattern: ${nameArg}`], [searchDir], 'safe', false, undefined, estimateDuration(command));
  }

  return makePreview('search-results', fullCmd, `Search for "${pattern}" in ${target}`, [`Pattern: ${pattern}`, `Scope: ${target}`], [target], 'safe', false, undefined, estimateDuration(command));
}

export function previewPackageCommand(command: string, args: string[]): CommandPreview {
  const subcommand = args[0] ?? '';
  const packages = args.slice(1).filter(a => !a.startsWith('-'));
  const fullCmd = `${command} ${args.join(' ')}`;

  const installVerbs: Record<string, string[]> = {
    npm: ['install', 'i', 'add', 'ci'], pnpm: ['install', 'i', 'add'], yarn: ['add', 'install'],
    pip: ['install'], pip3: ['install'], cargo: ['install', 'add'], brew: ['install'],
  };
  const removeVerbs: Record<string, string[]> = {
    npm: ['uninstall', 'remove', 'rm'], pnpm: ['remove', 'rm', 'uninstall'], yarn: ['remove'],
    pip: ['uninstall'], pip3: ['uninstall'], cargo: ['uninstall'], brew: ['uninstall', 'remove'],
  };

  const isInstall = (installVerbs[command] ?? []).includes(subcommand);
  const isRemove = (removeVerbs[command] ?? []).includes(subcommand);

  if (isInstall) {
    const pkgList = packages.length > 0 ? packages.join(', ') : 'project dependencies';
    return makePreview('package-info', fullCmd, `Install: ${pkgList}`, [`Package manager: ${command}`, `Packages: ${pkgList}`], [], 'safe', false, undefined, packages.length > 3 ? '~30s+' : '~10s');
  }
  if (isRemove) {
    const pkgList = packages.join(', ') || 'unknown';
    const dryRun = getDryRunFlag(`${command} ${subcommand}`);
    return makePreview('package-info', fullCmd, `Remove: ${pkgList}`, [`Uninstalling: ${pkgList}`], [], 'moderate', !!dryRun, dryRun ?? undefined, '~5s');
  }

  if (subcommand === 'run' || subcommand === 'exec') {
    const script = packages[0] ?? '';
    return makePreview('package-info', fullCmd, `Run script: ${script}`, [`Execute ${command} script: ${script}`], [], 'safe', false, undefined, 'varies');
  }

  if (subcommand === 'publish') {
    const dryRun = getDryRunFlag(`${command} publish`);
    return makePreview('package-info', fullCmd, 'Publish package to registry', ['Publishes the current package to the registry', 'This is a public action'], [], 'moderate', true, dryRun ?? '--dry-run');
  }

  return makePreview('package-info', fullCmd, `${command} ${subcommand}`, [`Execute: ${command} ${subcommand}`], [], 'safe', false);
}

export function previewNetworkCommand(command: string, args: string[]): CommandPreview {
  const nonFlags = args.filter(a => !a.startsWith('-'));
  const target = nonFlags[0] ?? '';
  const fullCmd = `${command} ${args.join(' ')}`;

  switch (command) {
    case 'curl': {
      const method = extractFlagValue(args, '-X') ?? 'GET';
      return makePreview('network', fullCmd, `${method} request to ${target}`, [`Method: ${method}`, `URL: ${target}`], [], 'safe', false, undefined, '~2s');
    }
    case 'wget':
      return makePreview('network', fullCmd, `Download ${target}`, [`Downloading: ${target}`], [], 'safe', false, undefined, 'varies');
    case 'ping':
      return makePreview('network', fullCmd, `Ping ${target}`, [`Send ICMP packets to ${target}`], [], 'safe', false, undefined, 'continuous');
    case 'ssh':
      return makePreview('network', fullCmd, `SSH connection to ${target}`, [`Connect to: ${target}`], [], 'safe', false, undefined, 'interactive');
    case 'scp': {
      const dest = nonFlags[1] ?? '';
      return makePreview('network', fullCmd, `Copy ${target} to ${dest}`, [`Source: ${target}`, `Destination: ${dest}`], [target, dest], 'safe', false, undefined, 'varies');
    }
    default:
      return makePreview('network', fullCmd, `${command} ${target}`, [`Execute network command: ${command}`], [], 'safe', false);
  }
}

export function previewDiskCommand(command: string, args: string[]): CommandPreview {
  const nonFlags = args.filter(a => !a.startsWith('-'));
  const target = nonFlags[0] ?? (command === 'df' ? 'all filesystems' : '.');
  const fullCmd = `${command} ${args.join(' ')}`;

  switch (command) {
    case 'df':
      return makePreview('disk-usage', fullCmd, `Show disk space for ${target}`, [`Report filesystem space usage`], [], 'safe', false, undefined, '< 1s');
    case 'du':
      return makePreview('disk-usage', fullCmd, `Show disk usage for ${target}`, [`Calculate directory sizes in ${target}`], [typeof target === 'string' ? target : '.'], 'safe', false, undefined, estimateDuration(command));
    case 'mount':
      return makePreview('disk-usage', fullCmd, 'Mount filesystem', nonFlags.length > 0 ? [`Mount: ${nonFlags.join(' ')}`] : ['Show mounted filesystems'], [], nonFlags.length > 0 ? 'moderate' : 'safe', false);
    case 'umount':
      return makePreview('disk-usage', fullCmd, `Unmount ${target}`, [`Unmount: ${target}`], [], 'moderate', false);
    default:
      return makePreview('disk-usage', fullCmd, `${command} ${target}`, [], [], 'safe', false);
  }
}

function previewProcessCommand(command: string, args: string[]): CommandPreview {
  const nonFlags = args.filter(a => !a.startsWith('-'));
  const fullCmd = `${command} ${args.join(' ')}`;

  switch (command) {
    case 'ps':
      return makePreview('process-list', fullCmd, 'List running processes', ['Shows process information'], [], 'safe', false, undefined, '< 1s');
    case 'top': case 'htop':
      return makePreview('process-list', fullCmd, 'Interactive process monitor', ['Live system resource display'], [], 'safe', false, undefined, 'interactive');
    case 'kill': {
      const pids = nonFlags;
      const signal = args.find(a => a.startsWith('-'))?.replace('-', '') ?? '15';
      const isForce = signal === '9' || signal === 'KILL';
      return makePreview('process-list', fullCmd, `${isForce ? 'Force kill' : 'Terminate'} process${pids.length > 1 ? 'es' : ''}: ${pids.join(', ')}`, [`Signal: ${signal}${isForce ? ' (SIGKILL)' : ' (SIGTERM)'}`, ...pids.map(p => `PID: ${p}`)], [], isForce ? 'moderate' : 'safe', false);
    }
    case 'killall': {
      const name = nonFlags[0] ?? '';
      return makePreview('process-list', fullCmd, `Kill all processes named: ${name}`, [`Terminates ALL processes matching: ${name}`], [], 'dangerous', false);
    }
    default:
      return makePreview('process-list', fullCmd, `${command}`, [], [], 'safe', false);
  }
}

function previewDockerCommand(command: string, args: string[]): CommandPreview {
  const subcommand = args[0] ?? '';
  const subArgs = args.slice(1).filter(a => !a.startsWith('-'));
  const fullCmd = `${command} ${args.join(' ')}`;

  switch (subcommand) {
    case 'run':
      return makePreview('process-list', fullCmd, `Run container: ${subArgs[0] ?? 'image'}`, [`Image: ${subArgs[0] ?? '?'}`, 'Creates and starts a new container'], [], 'safe', false, undefined, 'varies');
    case 'build':
      return makePreview('process-list', fullCmd, `Build image${subArgs[0] ? ': ' + subArgs[0] : ''}`, ['Build Docker image from Dockerfile'], [], 'safe', false, undefined, '~30s+');
    case 'stop':
      return makePreview('process-list', fullCmd, `Stop container: ${subArgs.join(', ')}`, subArgs.map(c => `Stop: ${c}`), [], 'safe', false);
    case 'rm':
      return makePreview('process-list', fullCmd, `Remove container: ${subArgs.join(', ')}`, subArgs.map(c => `Delete: ${c}`), [], 'moderate', false);
    case 'rmi':
      return makePreview('process-list', fullCmd, `Remove image: ${subArgs.join(', ')}`, subArgs.map(i => `Delete image: ${i}`), [], 'moderate', false);
    case 'system':
      if (args[1] === 'prune') {
        return makePreview('process-list', fullCmd, 'Remove all unused Docker resources', ['Removes stopped containers, unused networks, dangling images'], [], 'dangerous', true, '--dry-run');
      }
      return makePreview('process-list', fullCmd, `Docker system ${args[1] ?? ''}`, [], [], 'safe', false);
    case 'ps':
      return makePreview('process-list', fullCmd, 'List running containers', ['Shows running Docker containers'], [], 'safe', false, undefined, '< 1s');
    case 'logs':
      return makePreview('file-content', fullCmd, `Show logs for: ${subArgs[0] ?? 'container'}`, [`Container: ${subArgs[0] ?? '?'}`], [], 'safe', false);
    default:
      return makePreview('none', fullCmd, `docker ${subcommand}`, [`Execute: docker ${subcommand}`], [], 'safe', false);
  }
}

function previewPermissionCommand(command: string, args: string[]): CommandPreview {
  const nonFlags = args.filter(a => !a.startsWith('-'));
  const isRecursive = args.some(a => a === '-R' || a === '--recursive');
  const fullCmd = `${command} ${args.join(' ')}`;

  const target = nonFlags[nonFlags.length - 1] ?? '';
  const mode = nonFlags[0] ?? '';

  let summary = `${command} ${mode} on ${target}`;
  const details: string[] = [`Target: ${target}`, `Mode/Owner: ${mode}`];

  if (isRecursive) {
    details.push('Recursive: applies to all children');
    summary += ' (recursive)';
  }

  if (command === 'chmod' && mode === '777') {
    details.push('WARNING: 777 gives full permissions to all users');
    return makePreview('none', fullCmd, summary, details, [target], 'dangerous', false);
  }

  return makePreview('none', fullCmd, summary, details, [target], 'moderate', false);
}

// ═══ §7 UTILITY FUNCTIONS ═══

export function getDryRunFlag(command: string): string | null {
  const trimmed = command.trim();
  // Check compound keys first (e.g. "npm publish")
  for (const [key, flag] of Object.entries(DRY_RUN_FLAGS)) {
    if (trimmed.startsWith(key)) return flag;
  }
  // Then base command
  const base = trimmed.split(/\s+/)[0] ?? '';
  return DRY_RUN_FLAGS[base] ?? null;
}

export function estimateDuration(command: string): string {
  const base = command.trim().split(/\s+/)[0] ?? '';
  const fast = ['ls', 'cat', 'head', 'tail', 'echo', 'pwd', 'cd', 'wc', 'ps', 'df', 'which', 'whoami', 'date', 'hostname'];
  const medium = ['grep', 'find', 'cp', 'mv', 'rm', 'chmod', 'chown', 'tar', 'gzip', 'du', 'git', 'rg', 'fd'];
  const slow = ['npm', 'pnpm', 'yarn', 'pip', 'cargo', 'docker', 'rsync', 'wget', 'curl', 'scp', 'make', 'cmake'];
  const interactive = ['ssh', 'top', 'htop', 'vim', 'nano', 'less', 'man'];

  if (fast.includes(base)) return '< 1s';
  if (medium.includes(base)) return '~1-5s';
  if (slow.includes(base)) return '~10s+';
  if (interactive.includes(base)) return 'interactive';
  return 'varies';
}

function extractFlagValue(args: string[], flag: string): string | null {
  const idx = args.indexOf(flag);
  if (idx !== -1 && idx + 1 < args.length) return args[idx + 1]!;
  // Check --flag=value form
  const eqForm = args.find(a => a.startsWith(flag + '='));
  if (eqForm) return eqForm.slice(flag.length + 1);
  return null as string | null;
}

function makePreview(
  type: PreviewType, command: string, summary: string, details: string[],
  affectedPaths: string[], riskLevel: 'safe' | 'moderate' | 'dangerous',
  canDryRun: boolean, dryRunFlag?: string, estimatedDuration?: string,
): CommandPreview {
  return { type, command: command.trim(), summary, details, affectedPaths, riskLevel, canDryRun, dryRunFlag, estimatedDuration };
}
